import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import fileUploadSecurity from '../../services/fileUploadSecurity';
import SecurityValidator from '../../utils/securityValidation';

const isImageFile = (file) => {
  if (file.file instanceof File) {
    return file.file.type.startsWith('image/');
  }
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(
    file.localPath || file.AttachmentName || ''
  );
};
const ImageUpload = ({
  onFilesSelected,
  folderPath = 'uploads',
  existingFiles = [],
  maxFiles = 0,
  disabled = false,
  rejectedExtensions = [] // Deprecated: use fileUploadSecurity service instead
}) => {
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  /* --------------------------------------------
     Initialize with existing backend attachments
  --------------------------------------------- */
  useEffect(() => {
    if (existingFiles?.length) {
      const mapped = existingFiles.map((file) => {
        let preview = file.AttachmentData || file.preview || '';
        if (preview && !preview.startsWith('data:') && !preview.startsWith('http')) {
          const ext = (file.AttachmentName || file.localPath || '').split('.').pop()?.toLowerCase();
          const mimeMap = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp', svg: 'image/svg+xml', pdf: 'application/pdf', xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', csv: 'text/csv', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };
          const mime = mimeMap[ext] || 'application/octet-stream';
          preview = `data:${mime};base64,${preview}`;
        }
        return {
          preview,
        localPath: file.AttachmentName || file.localPath,
          isExisting: true,
          attachmentId: file.AttachmentId
        };
      });
      setFiles(mapped);
    }
  }, [existingFiles]);

  /* --------------------------------------------
     Read file as base64
  --------------------------------------------- */
  const readFile = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) =>
        resolve({
          file,
          preview: e.target.result,
          localPath: `${folderPath}/${Date.now()}_${file.name}`,
          folderPath
        });
      reader.readAsDataURL(file);
    });

  /* --------------------------------------------
     Secure file processing with comprehensive validation
  --------------------------------------------- */
  const processFiles = async (selected) => {
    setIsValidating(true);
    setFileError('');
    setValidationErrors([]);
    
    try {
      // Check file count limits
      if (maxFiles > 0 && files.length + selected.length > maxFiles) {
        setFileError(`Maximum ${maxFiles} files allowed`);
        setIsValidating(false);
        return;
      }

      // Validate each file using security service
      console.log('[FileUpload] Starting security validation for', selected.length, 'files');
      const validationResults = await fileUploadSecurity.validateFiles(selected);
      const errors = [];
      const validFiles = [];
      
      for (const result of validationResults) {
        console.log('[FileUpload] Validation result for', result.file.name, ':', result.validation);
        
        if (result.validation.isValid) {
          // Additional client-side security checks
          const additionalCheck = await performAdditionalSecurityChecks(result.file);
          if (additionalCheck.isValid) {
            validFiles.push(result.file);
            console.log('[FileUpload] File passed all security checks:', result.file.name);
          } else {
            errors.push({
              fileName: result.validation.sanitizedFileName || result.file.name,
              errors: additionalCheck.errors
            });
            console.warn('[FileUpload] File failed additional security checks:', result.file.name, additionalCheck.errors);
          }
        } else {
          errors.push({
            fileName: result.validation.sanitizedFileName || result.file.name,
            errors: result.validation.errors
          });
          console.warn('[FileUpload] File failed validation:', result.file.name, result.validation.errors);
        }
        
        // Log warnings if any
        if (result.validation.warnings.length > 0) {
          console.warn(`[FileUpload] File warnings for ${result.file.name}:`, result.validation.warnings);
        }
      }

      // Display validation errors
      if (errors.length > 0) {
        setValidationErrors(errors);
        setIsValidating(false);
        return;
      }

      // Process valid files
      if (validFiles.length > 0) {
        const processed = await Promise.all(
          validFiles.map(file => readSecureFile(file))
        );

        setFiles((prev) => {
          const updated = [...prev, ...processed];
          onFilesSelected?.(updated);
          return updated;
        });
      }

    } catch (error) {
      console.error('File processing error:', error);
      setFileError(`File processing failed: ${error.message}`);
    }
    
    setIsValidating(false);
  };

  /**
   * Additional client-side security checks to prevent bypass
   * @param {File} file - File to validate
   * @returns {Promise<Object>} - Additional validation result
   */
  const performAdditionalSecurityChecks = async (file) => {
    try {
      // Use comprehensive security validator
      const securityResult = SecurityValidator.validateFile(file);
      
      if (!securityResult.isValid) {
        SecurityValidator.logSecurityEvent('FILE_UPLOAD_BLOCKED', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          errors: securityResult.errors
        });
        
        return {
          isValid: false,
          errors: securityResult.errors
        };
      }
    } catch (securityError) {
      console.warn('Security validation failed:', securityError);
      // Don't fail on security validation errors for images
      if (file.type.startsWith('image/')) {
        console.warn('Allowing image file despite security validation failure');
      } else {
        return {
          isValid: false,
          errors: [`Security validation failed: ${securityError.message}`]
        };
      }
    }
        
    // Additional header validation - make it optional for images
    try {
      const header = await readFileHeader(file);
      if (!isValidFileHeader(header, file.type)) {
        // For images, just warn instead of blocking
        if (file.type.startsWith('image/')) {
          console.warn('Image header validation failed, but allowing upload:', file.name);
          return {
            isValid: true,
            errors: [],
            warnings: ['Image header validation failed but upload allowed']
          };
        }
        
        SecurityValidator.logSecurityEvent('FILE_HEADER_MISMATCH', {
          fileName: file.name,
          declaredType: file.type,
          headerBytes: Array.from(header.slice(0, 8))
        });
        
        return {
          isValid: false,
          errors: ['File header does not match declared type - possible file spoofing']
        };
      }
    } catch (headerError) {
      console.warn('Header validation failed:', headerError);
      // Don't fail on header validation errors for images, just warn
      if (file.type.startsWith('image/')) {
        return {
          isValid: true,
          errors: [],
          warnings: [`Header validation failed but allowing image: ${headerError.message}`]
        };
      }
      return {
        isValid: false,
        errors: [`Header validation failed: ${headerError.message}`]
      };
    }
    
    try {
      SecurityValidator.logSecurityEvent('FILE_UPLOAD_VALIDATED', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });
    } catch (logError) {
      console.warn('Security logging failed:', logError);
    }
    
    return {
      isValid: true,
      errors: []
    };
  };
  
  /**
   * Read file header bytes
   * @param {File} file - File to read
   * @returns {Promise<Uint8Array>} - File header bytes
   */
  const readFileHeader = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const blob = file.slice(0, 16); // Read first 16 bytes
      
      reader.onload = () => {
        const bytes = new Uint8Array(reader.result);
        resolve(bytes);
      };
      
      reader.onerror = () => reject(new Error('Failed to read file header'));
      reader.readAsArrayBuffer(blob);
    });
  };
  
  /**
   * Validate file header matches expected type
   * @param {Uint8Array} header - File header bytes
   * @param {string} mimeType - Declared MIME type
   * @returns {boolean} - True if header matches
   */
  const isValidFileHeader = (header, mimeType) => {
    const signatures = {
      'image/jpeg': [[0xFF, 0xD8, 0xFF]], // Standard JPEG
      'image/jpg': [[0xFF, 0xD8, 0xFF]],  // Same as JPEG
      'image/png': [[0x89, 0x50, 0x4E, 0x47]],
      'image/gif': [[0x47, 0x49, 0x46, 0x38]],
      'image/webp': [[0x52, 0x49, 0x46, 0x46]],
      'image/bmp': [[0x42, 0x4D]],
      'application/pdf': [[0x25, 0x50, 0x44, 0x46]]
    };
    
    const expectedSigs = signatures[mimeType];
    if (!expectedSigs) return true; // No signature check for unknown types
    
    // More lenient JPEG validation - check for any JPEG signature
    if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
      // Check for various JPEG signatures
      return header[0] === 0xFF && header[1] === 0xD8; // Just check FF D8 start
    }
    
    return expectedSigs.some(sig => {
      for (let i = 0; i < sig.length && i < header.length; i++) {
        if (header[i] !== sig[i]) return false;
      }
      return true;
    });
  };

  /* --------------------------------------------
     Secure file reading with sanitization
  --------------------------------------------- */
  const readSecureFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          // Validate the base64 result
          const result = e.target.result;
          if (!result || typeof result !== 'string') {
            reject(new Error('Failed to read file content'));
            return;
          }
          
          // Additional security validation on the base64 content
          if (!result.startsWith('data:')) {
            reject(new Error('Invalid file data format'));
            return;
          }
          
          // Verify MIME type in data URL matches file type (more lenient)
          const mimeMatch = result.match(/^data:([^;]+)/);
          if (mimeMatch && file.type && mimeMatch[1] !== file.type) {
            console.warn('MIME type mismatch in data URL:', mimeMatch[1], 'vs', file.type);
            // Don't fail on MIME mismatch, just warn
          }
          
          // Generate secure file name
          const timestamp = Date.now();
          const secureRandom = Array.from(
            crypto.getRandomValues(new Uint8Array(8)),
            b => b.toString(16).padStart(2, '0')
          ).join('');
          
          const cleanedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').substring(0, 50);
          const sanitizedName = `${timestamp}_${secureRandom}_${cleanedName}`;
          
          resolve({
            file,
            preview: result,
            localPath: `${folderPath}/${sanitizedName}`,
            folderPath,
            originalName: file.name,
            sanitizedName: sanitizedName,
            fileSize: file.size,
            mimeType: file.type,
            isSecure: true, // Mark as secure validated
            validationTimestamp: new Date().toISOString(),
            checksum: btoa(file.name + file.size + file.type), // Basic integrity check
            // Add required security validation markers for API
            IsSecureValidated: true,
            ValidationChecksum: btoa(file.name + file.size + file.type),
            ValidationTimestamp: new Date().toISOString(),
            ClientValidationPassed: true
          });
        } catch (processError) {
          console.error('Error processing file:', processError);
          reject(new Error(`File processing failed: ${processError.message}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('File reading failed'));
      };
      
      try {
        reader.readAsDataURL(file);
      } catch (readerError) {
        reject(new Error(`FileReader error: ${readerError.message}`));
      }
    });
  };

  const onSelect = (e) =>
    processFiles(Array.from(e.target.files));
  const onDrop = (e) => {
    e.preventDefault();
    processFiles(Array.from(e.dataTransfer.files));
  };

  /* --------------------------------------------
     Click behavior
  --------------------------------------------- */
  const getFileName = (file) => {
    if (file.file instanceof File) return file.file.name;
    const p = file.localPath || file.AttachmentName || '';
    return p.split('/').pop() || 'attachment';
  };

  const handleFileClick = (file) => {
    if (isImageFile(file)) {
      setPreviewImage(file.preview);
      setPreviewOpen(true);
    } else {
      downloadFile(file);
    }
  };

  const downloadFile = (file) => {
    const link = document.createElement('a');
    link.href = file.preview;
    link.download = getFileName(file);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      onFilesSelected?.(updated);
      return updated;
    });
  };

  /* --------------------------------------------
     UI
  --------------------------------------------- */
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Attachments
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {!disabled && (
  <Box
    onDrop={onDrop}
    onDragOver={(e) => e.preventDefault()}
    sx={{
      border: '2px dashed #ccc',
      borderRadius: '8px',
      padding: '20px',
      textAlign: 'center',
      width: '150px',
      height: '150px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#fafafa'
    }}
  >
    <Typography variant="body2">
      Drag File Here or
    </Typography>

    <Button
      variant="contained"
      disabled={isValidating}
      sx={{
        mt: 1,
        backgroundColor: '#800040',
        color: '#fff',
        textTransform: 'none'
      }}
      onClick={() =>
        document.getElementById('fileInput').click()
      }
    >
      {isValidating ? 'Validating...' : 'Add File'}
    </Button>

    <input
      id="fileInput"
      type="file"
      multiple
      hidden
      accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.xls,.xlsx,.txt,.csv"
      onChange={onSelect}
    />
  </Box>
)}

        {files.map((file, index) => (
          <Box
            key={index}
            sx={{
              width: 150,
              height: 150,
              borderRadius: 2,
              position: 'relative',
              cursor: 'pointer',
              backgroundColor: '#f2f2f2'
            }}
            onClick={() => handleFileClick(file)}
          >
            {isImageFile(file) ? (
              <img
                src={file.preview}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1
                }}
              >
                <InsertDriveFileIcon fontSize="large" />
                <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'center', wordBreak: 'break-all', fontSize: '0.65rem' }}>
                  {getFileName(file)}
                </Typography>
              </Box>
            )}

            {!disabled && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  backgroundColor: '#fff'
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      {fileError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {fileError}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {validationErrors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>File: {error.fileName}</Typography>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                {error.errors.map((err, errIndex) => (
                  <li key={errIndex}>
                    <Typography variant="body2">{err}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          ))}
        </Box>
      )}

      {isValidating && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Validating files for security threats...
        </Alert>
      )}

      {/* Image Preview */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
      >
        <DialogContent sx={{ position: 'relative', p: 1 }}>
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: '#fff', zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {previewImage ? (
            <img
              src={previewImage}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
          ) : (
            <Typography sx={{ p: 3 }}>No preview available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImageUpload;